import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, TextField, FormHelperText } from '@mui/material';
import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { CloseConsultancyModalProps } from '../../../interfaces/Consultancy';
import axios from 'axios';
import { useToast } from '../../../hooks/useToast';
import { getConfig } from '../../../config/env';

const CloseConsultancyModal: React.FC<CloseConsultancyModalProps> = ({ open, onClose, onConsultancyClosed, consultancyId, paymentId, pinner_first_name }) => {
    const url = getConfig().BASE_URL;
    const { t } = useTranslation();
    const authToken = sessionStorage.getItem('authToken');
    const [code, setCode] = useState<string>('');
    const [error, setError] = useState<string>('');
    const { consultancyClosedSuccess, consultancyClosedErrorCode, consultancyClosedError } = useToast();

    const handleClose = () => {
        setError('');
        setCode('');
        onClose();
    };

    const handleCloseConsultancy = async () => {
        try {
            const response = await axios.post(`${url}/billing/complete-consultation/`, {
                payment_id: paymentId,
                secret_code: code
            }, {
                headers: {
                    Authorization: `Bearer ${authToken}`
                }
            });
            if (response.status === 200) {
                consultancyClosedSuccess();

                if (onConsultancyClosed && consultancyId) {
                    onConsultancyClosed(consultancyId);
                }
            };
            setCode('');
            onClose();
        } catch (error: any) {
            if (error.response.status === 400) {
                consultancyClosedErrorCode();
            } else {
                consultancyClosedError();
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6" align="left" sx={{ pb: 0 }}>{t('consultancy:modal_close_title')}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" align="left" sx={{ mb: 2 }}>{t('consultancy:modal_close_description')}<span style={{ fontWeight: 'bold' }}>{pinner_first_name}</span>{t('consultancy:modal_close_description_2')}</Typography>
                <TextField
                    rows={1}
                    fullWidth
                    variant="outlined"
                    placeholder={t('consultancy:modal_code_placeholder')}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    error={!!error}
                    sx={{
                        margin: 0,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: '#E0E0E0',
                            },
                            '&:hover fieldset': {
                                borderColor: '#E0E0E0',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#E0E0E0',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: '#000000',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#000000',
                        },
                        '& .MuiInputBase-input.Mui-disabled': {
                            color: '#000000',
                        },
                        '& .MuiFormHelperText-root': {
                            textAlign: 'right',
                            marginRight: '5px',
                        }
                    }}
                />
                {error && (
                    <FormHelperText error>
                        {error}
                    </FormHelperText>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2, px: 3 }}>
                <Button
                    fullWidth
                    variant="contained"
                    sx={{
                        backgroundColor: '#E9495A',
                        color: '#fff',
                        '&:hover': { backgroundColor: "#E6263B" }
                    }}
                    onClick={handleCloseConsultancy}
                >
                    {t('consultancy:modal_close_button')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CloseConsultancyModal;